import React, { useEffect, useState } from 'react';
const makeIcon = (symbol) => function Icon({ className = '' }) { return <span className={className} aria-hidden="true">{symbol}</span>; };
const FaArrowRight = makeIcon('→');
const FaBars = makeIcon('☰');
const FaCheck = makeIcon('✓');
const FaEnvelope = makeIcon('✉');
const FaFacebookF = makeIcon('f');
const FaInstagram = makeIcon('◎');
const FaLinkedinIn = makeIcon('in');
const FaMapMarkerAlt = makeIcon('⌖');
const FaAddress = makeIcon('●');
const FaPhoneAlt = makeIcon('☎');
const FaQuoteLeft = makeIcon('❝');
const FaTimes = makeIcon('×');
const FaWhatsapp = makeIcon('◉');

const navItems = [
  ['Home', 'home'],
  ['About', 'about'],
  ['Products', 'products'],
  ['Services', 'services'],
  ['Reviews', 'reviews'],
  ['Contact', 'contact'],
];

const products = [
  {
    name: 'Industrial Packaging Range',
    description: 'Durable packaging materials selected for transport, storage, and everyday business operations.',
    image: '/images/product-packaging.svg',
    tag: 'Popular',
  },
  {
    name: 'Workspace Essentials',
    description: 'Curated workplace supplies that keep teams organised, equipped, and productive.',
    image: '/images/product-office.svg',
    tag: 'Business',
  },
  {
    name: 'Facility Care Products',
    description: 'Dependable cleaning and hygiene products for offices, stores, and shared facilities.',
    image: '/images/product-cleaning.svg',
    tag: 'Essential',
  },
];

const services = [
  {
    number: '01',
    title: 'Commercial Supply',
    text: 'Consistent product supply, practical order quantities, and dependable delivery for business customers.',
  },
  {
    number: '02',
    title: 'Requirement-Based Sourcing',
    text: 'Share your requirement and we will identify suitable products aligned with your specifications and budget.',
  },
  {
    number: '03',
    title: 'Account Support',
    text: 'Clear updates, responsive assistance, and practical guidance throughout the complete order journey.',
  },
];

const reviews = [
  {
    name: 'Aarav Mehta',
    role: 'Operations Manager, Crestline Retail',
    text: 'The team is responsive and dependable. Our recurring supply orders are handled smoothly and always with clear updates.',
  },
  {
    name: 'Priya Nair',
    role: 'Founder, Nova Workspace',
    text: 'Salve Enterprises helped us consolidate multiple office requirements into one simple order. Excellent service.',
  },
  {
    name: 'Rohan Kulkarni',
    role: 'Purchase Lead, Axis Build Studio',
    text: 'Good quality, practical pricing, and very professional communication. We are happy to continue working with them.',
  },
];

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const submitForm = (event) => {
    event.preventDefault();
    setSubmitted(true);
    event.currentTarget.reset();
  };

  return (
    <div className="site-shell">
      <header className={`header ${scrolled ? 'header--scrolled' : ''}`}>
        <div className="container header__inner">
          <a className="brand" href="#home" onClick={closeMenu} aria-label="Salve Enterprises home">
            <img src="/images/logo.svg" alt="Salve Enterprises" />
          </a>

          <button
            className="menu-toggle"
            type="button"
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((value) => !value)}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>

          <nav className={`nav ${menuOpen ? 'nav--open' : ''}`} aria-label="Primary navigation">
            {navItems.map(([label, id]) => (
              <a key={id} href={`#${id}`} onClick={closeMenu}>{label}</a>
            ))}
            <a className="button button--small" href="#contact" onClick={closeMenu}>Get a Quote</a>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero" id="home">
          <div className="hero__orb hero__orb--one" />
          <div className="hero__orb hero__orb--two" />
          <div className="container hero__grid">
            <div className="hero__content reveal">
              <span className="eyebrow">Built for business. Ready to deliver.</span>
              <h1>Smart commercial supplies for modern businesses.</h1>
              <p>
                Salve Enterprises helps businesses source everyday essentials with dependable quality,
                competitive pricing, responsive service, and reliable delivery.
              </p>
              <div className="hero__actions">
                <a className="button" href="#products">Explore Products <FaArrowRight /></a>
                <a className="text-link" href="#contact">Talk to our team</a>
              </div>
              <div className="hero__trust">
                <div><strong>400+</strong><span>Business orders</span></div>
                <div><strong>97%</strong><span>Repeat customers</span></div>
                <div><strong>24h</strong><span>Response commitment</span></div>
              </div>
            </div>
            <div className="hero__visual reveal">
              <div className="hero-card hero-card--main">
                <img src="/images/hero-business.svg" alt="Business supply and service illustration" />
              </div>
              <div className="hero-card hero-card--floating">
                <span className="hero-card__icon"><FaCheck /></span>
                <div><strong>Trusted fulfilment</strong><small>Quality checked orders</small></div>
              </div>
            </div>
          </div>
        </section>

        <section className="section about" id="about">
          <div className="container about__grid">
            <div className="about__image-wrap reveal">
              <img src="/images/about-team.svg" alt="Salve Enterprises team illustration" />
              <div className="experience-badge"><strong>7+</strong><span>Years serving businesses</span></div>
            </div>
            <div className="section-copy reveal">
              <span className="eyebrow">About us</span>
              <h2>Reliable sourcing, professional service, and lasting partnerships.</h2>
              <p>
                We help offices, retailers, workshops, and institutions procure essential products without unnecessary delays. Our team focuses on practical solutions, responsive communication, and consistent fulfilment.
              </p>
              <ul className="check-list">
                <li><FaCheck /> Quality-focused sourcing</li>
                <li><FaCheck /> Flexible quantities and competitive pricing</li>
                <li><FaCheck /> Friendly support before and after purchase</li>
              </ul>
              <a className="button button--outline" href="#contact">Know More <FaArrowRight /></a>
            </div>
          </div>
        </section>

        <section className="section section--soft" id="products">
          <div className="container">
            <div className="section-heading reveal">
              <div><span className="eyebrow">Our products</span><h2>Useful products, chosen for real business needs.</h2></div>
              <p>Our sample range can be tailored based on your industry, quantity, and delivery requirement.</p>
            </div>
            <div className="card-grid products-grid">
              {products.map((product) => (
                <article className="product-card reveal" key={product.name}>
                  <div className="product-card__image">
                    <span className="pill">{product.tag}</span>
                    <img src={product.image} alt={product.name} />
                  </div>
                  <div className="product-card__body">
                    <h3>{product.name}</h3>
                    <p>{product.description}</p>
                    <a href="#contact">Request details <FaArrowRight /></a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section services" id="services">
          <div className="container">
            <div className="section-heading section-heading--center reveal">
              <div><span className="eyebrow">Our services</span><h2>Support that keeps procurement simple.</h2></div>
              <p>From first enquiry to repeat fulfilment, we stay accessible and focused on your business priorities.</p>
            </div>
            <div className="services-grid">
              {services.map((service) => (
                <article className="service-card reveal" key={service.number}>
                  <span className="service-card__number">{service.number}</span>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                  <a href="#contact" aria-label={`Enquire about ${service.title}`}><FaArrowRight /></a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section reviews" id="reviews">
          <div className="container">
            <div className="section-heading reveal">
              <div><span className="eyebrow">Client reviews</span><h2>What our customers say about working with us.</h2></div>
              <p>These sample testimonials can be replaced with verified customer feedback.</p>
            </div>
            <div className="reviews-grid">
              {reviews.map((review) => (
                <article className="review-card reveal" key={review.name}>
                  <FaQuoteLeft className="review-card__quote" />
                  <p>“{review.text}”</p>
                  <div className="review-card__person">
                    <span>{review.name.split(' ').map((name) => name[0]).join('')}</span>
                    <div><strong>{review.name}</strong><small>{review.role}</small></div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section contact" id="contact">
          <div className="container contact__grid">
            <div className="contact__info reveal">
              <span className="eyebrow eyebrow--light">Contact us</span>
              <h2>Let’s discuss what your business needs.</h2>
              <p>Share your requirement and our team will respond with the next steps.</p>
              <div className="contact-list">
                <a href="tel:+917620266086"><span><FaPhoneAlt /></span><div><small>Call us</small><strong>+91 76202 66086 </strong></div></a>
                <a href="mailto:admin@salveenterprises.online"><span><FaEnvelope /></span><div><small>Email us</small><strong>admin@salveenterprises.online</strong></div></a>
                <div><span><FaMapMarkerAlt /></span><div><small>Business name</small><strong>Salve Enterprises</strong></div></div>
                <div><span><FaAddress /></span><div><small>Business address</small><strong>Near Ambika Mandir, Ghorpade Vasti, Kadam Wak Wasti, Krushna Nagar, Kadamwak Wasti, PUNE, MAHARASHTRA, 412201</strong></div></div>

              </div>
            </div>

            <form className="contact-form reveal" onSubmit={submitForm}>
              <div className="form-row">
                <label>Full name<input type="text" name="name" placeholder="Your name" required /></label>
                <label>Phone number<input type="tel" name="phone" placeholder="+91 76202 66086" required /></label>
              </div>
              <label>Email address<input type="email" name="email" placeholder="you@company.com" required /></label>
              <label>Interested in
                <select name="interest" defaultValue="">
                  <option value="" disabled>Select an option</option>
                  <option>Product supply</option>
                  <option>Custom procurement</option>
                  <option>Business support</option>
                  <option>Other requirement</option>
                </select>
              </label>
              <label>Message<textarea name="message" rows="5" placeholder="Tell us about your requirement" required /></label>
              <button className="button button--full" type="submit">Send Enquiry <FaArrowRight /></button>
              {submitted && <p className="form-success" role="status">Thank you! Your sample enquiry has been recorded.</p>}
            </form>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer__top">
          <div className="footer__brand">
            <img src="/images/logo-light.svg" alt="Salve Enterprises" />
            <p>Commercial supplies, responsible service, and dependable fulfilment for growing organisations.</p>
            <div className="socials">
              <a href="https://www.facebook.com" aria-label="Facebook"><FaFacebookF /></a>
              <a href="https://www.instagram.com" aria-label="Instagram"><FaInstagram /></a>
              <a href="https://www.linkedin.com" aria-label="LinkedIn"><FaLinkedinIn /></a>
              <a href="https://wa.me/917620266086" aria-label="WhatsApp"><FaWhatsapp /></a>
            </div>
          </div>
          <div className="footer__links">
            <h3>Quick links</h3>
            {navItems.slice(1).map(([label, id]) => <a href={`#${id}`} key={id}>{label}</a>)}
          </div>
          <div className="footer__links">
            <h3>Business hours</h3>
            <p>Monday – Saturday</p>
            <p>9:30 AM – 6:30 PM</p>
            <p>Sunday: Closed</p>
          </div>
        </div>
        <div className="container footer__bottom">
          <span>© {new Date().getFullYear()} Salve Enterprises. All rights reserved.</span>
          <span>Supplying progress. Building trust.</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
